/**
 * Service Instance Management Hook
 * Handles multi-instance service selection and management
 */

import { useCallback, useState } from "react";

export interface ServiceInstance {
  instanceId: number;
  serviceId: number;
  serviceName: string;
  customName?: string;
}

export const useServiceInstances = () => {
  const [serviceInstances, setServiceInstances] = useState<ServiceInstance[]>([]);
  const [activeInstanceId, setActiveInstanceId] = useState<number | null>(null);
  const [nextInstanceId, setNextInstanceId] = useState(1);

  const addServiceInstance = useCallback(
    (serviceId: number, serviceName: string) => {
      const instanceId = nextInstanceId;
      setServiceInstances((prev) => [
        ...prev,
        {
          instanceId,
          serviceId,
          serviceName,
        },
      ]);
      setNextInstanceId((prev) => prev + 1);
      setActiveInstanceId(instanceId);
      return instanceId;
    },
    [nextInstanceId]
  );

  const removeServiceInstance = useCallback(
    (instanceId: number) => {
      setServiceInstances((prev) => prev.filter((inst) => inst.instanceId !== instanceId));
      setActiveInstanceId((current) => {
        if (current === instanceId) {
          const remaining = serviceInstances.filter((inst) => inst.instanceId !== instanceId);
          return remaining.length > 0 ? remaining[remaining.length - 1].instanceId : null;
        }
        return current;
      });
    },
    [serviceInstances]
  );

  const renameServiceInstance = useCallback((instanceId: number, customName: string) => {
    setServiceInstances((prev) =>
      prev.map((inst) => (inst.instanceId === instanceId ? { ...inst, customName } : inst))
    );
  }, []);

  const getCurrentInstance = useCallback((): ServiceInstance | null => {
    if (activeInstanceId === null) return null;
    return serviceInstances.find((inst) => inst.instanceId === activeInstanceId) || null;
  }, [activeInstanceId, serviceInstances]);

  const getInstancesByServiceId = useCallback(
    (serviceId: number): ServiceInstance[] => {
      return serviceInstances.filter((inst) => inst.serviceId === serviceId);
    },
    [serviceInstances]
  );

  const getInstanceLabel = useCallback(
    (instance: ServiceInstance): string => {
      const sameServiceInstances = serviceInstances.filter(
        (inst) => inst.serviceId === instance.serviceId
      );

      if (instance.customName) {
        return instance.customName;
      }

      if (sameServiceInstances.length > 1) {
        const index = sameServiceInstances.findIndex(
          (inst) => inst.instanceId === instance.instanceId
        );
        return `${instance.serviceName} ${index + 1}`;
      }

      return instance.serviceName;
    },
    [serviceInstances]
  );

  const loadInstances = useCallback((instances: ServiceInstance[], maxId: number) => {
    setServiceInstances(instances);
    setNextInstanceId(maxId + 1);
    if (instances.length > 0) {
      setActiveInstanceId(instances[0].instanceId);
    }
  }, []);

  return {
    serviceInstances,
    activeInstanceId,
    setActiveInstanceId,
    addServiceInstance,
    removeServiceInstance,
    renameServiceInstance,
    getCurrentInstance,
    getInstancesByServiceId,
    getInstanceLabel,
    loadInstances,
  };
};
